import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { categories } from "../../data/categories.tsx";
import { useBudgets } from "../../hooks/useBudgets.tsx";
import { useTransactions } from "../../hooks/useTransactions.tsx";
import { useCurrencyList } from "../../hooks/useCurrencyList.tsx";
import { Budget } from "../../types/budget.tsx";
import Input from "../../components/ui/input.tsx";
import Button from "../../components/ui/button.tsx";
import Label from "../../components/ui/label.tsx";
import { SimpleSelect } from "../../components/ui/select-shadcn.tsx";
import Modal from "../../components/Modal/index.tsx";
import { Plus, Pencil, Trash2 } from "lucide-react";

const DEFAULT_CURRENCY = "AED";

type FormState = {
  category: string;
  target: string;
  currency: string;
};

type InlineError = {
  category?: string;
  target?: string;
};

type BudgetRow = Budget & {
  currency: string;
  spent: number;
  remaining: number;
  progress: number;
};

const emptyForm = (currency: string): FormState => ({
  category: categories[0]?.id ?? "",
  target: "",
  currency,
});

const categoryLookup = categories.reduce<Record<string, string>>((acc, cat) => {
  acc[cat.id] = cat.name;
  return acc;
}, {});

const formatAmount = (value: number, currency: string) => {
  const safe = Number.isFinite(value) ? value : 0;
  return `${currency} ${safe.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export default function Budgets() {
  const { budgets, addBudget, updateBudget, removeBudget } = useBudgets();
  const { transactions } = useTransactions();
  const { currencies } = useCurrencyList();

  const currencyOptions = useMemo(() => {
    if (currencies.length === 0) {
      return [{ label: DEFAULT_CURRENCY, value: DEFAULT_CURRENCY }];
    }
    return currencies.map((currency) => ({
      label: `${currency.code} – ${currency.name}`,
      value: currency.code,
    }));
  }, [currencies]);

  const [form, setForm] = useState<FormState>(() =>
    emptyForm(currencyOptions[0]?.value || DEFAULT_CURRENCY)
  );
  const [editingBudget, setEditingBudget] = useState<Budget | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState<InlineError>({});
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!form.currency && currencyOptions.length > 0) {
      setForm((prev) => ({ ...prev, currency: currencyOptions[0].value }));
    }
  }, [currencyOptions, form.currency]);

  const expensesByKey = useMemo(() => {
    return transactions.reduce<Record<string, number>>((acc, tx) => {
      if (!tx || tx.type !== "expense") return acc;
      const currency = (tx.currency || DEFAULT_CURRENCY).toUpperCase();
      const category = tx.category || "miscellaneous";
      const key = `${category}:${currency}`;
      const amount = Math.abs(Number(tx.amount) || 0);
      acc[key] = (acc[key] || 0) + amount;
      return acc;
    }, {});
  }, [transactions]);

  const rows: BudgetRow[] = useMemo(() => {
    return budgets
      .map((budget) => {
        const currency = (budget.currency || DEFAULT_CURRENCY).toUpperCase();
        const key = `${budget.category}:${currency}`;
        const spent = expensesByKey[key] || 0;
        const remaining = budget.target - spent;
        const progress =
          budget.target > 0 ? Math.min(100, (spent / budget.target) * 100) : 0;
        return {
          ...budget,
          currency,
          spent,
          remaining,
          progress,
        };
      })
      .sort((a, b) => {
        const nameA = categoryLookup[a.category] || a.category;
        const nameB = categoryLookup[b.category] || b.category;
        return nameA.localeCompare(nameB);
      });
  }, [budgets, expensesByKey]);

  const visibleRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (needle === "") return rows;
    return rows.filter((row) => {
      const catName = (
        categoryLookup[row.category] || row.category
      ).toLowerCase();
      return (
        catName.includes(needle) || row.currency.toLowerCase().includes(needle)
      );
    });
  }, [rows, searchText]);

  const totalsByCurrency = useMemo(() => {
    return rows.reduce<Record<string, { allocated: number; spent: number }>>(
      (acc, row) => {
        if (!acc[row.currency]) {
          acc[row.currency] = { allocated: 0, spent: 0 };
        }
        acc[row.currency].allocated += row.target;
        acc[row.currency].spent += row.spent;
        return acc;
      },
      {}
    );
  }, [rows]);

  const resetForm = (currency?: string) => {
    const nextCurrency =
      currency ||
      form.currency ||
      currencyOptions[0]?.value ||
      DEFAULT_CURRENCY;
    setForm(emptyForm(nextCurrency));
    setErrors({});
    setEditingBudget(null);
  };

  const closeModal = (currency?: string) => {
    setShowModal(false);
    resetForm(currency);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: InlineError = {};
    if (!form.category) {
      nextErrors.category = "Choose a category";
    }
    const parsedTarget = Number(form.target);
    if (!Number.isFinite(parsedTarget) || parsedTarget <= 0) {
      nextErrors.target = "Enter a positive amount";
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const normalizedCurrency = (
      form.currency || DEFAULT_CURRENCY
    ).toUpperCase();
    const payload = {
      category: form.category,
      target: Number(parsedTarget.toFixed(2)),
      currency: normalizedCurrency,
    };

    if (editingBudget) {
      updateBudget(editingBudget.id, payload);
      closeModal(normalizedCurrency);
      return;
    }

    const existing = budgets.find(
      (budget) =>
        budget.category === payload.category &&
        (budget.currency || DEFAULT_CURRENCY).toUpperCase() === payload.currency
    );

    if (existing) {
      updateBudget(existing.id, payload);
      closeModal(normalizedCurrency);
      return;
    }

    addBudget(payload);
    closeModal(normalizedCurrency);
  };

  const handleRemove = (id: string) => {
    removeBudget(id);
    if (editingBudget?.id === id) {
      closeModal();
    }
  };

  const openCreateModal = () => {
    setEditingBudget(null);
    setErrors({});
    const preferredCurrency =
      form.currency || currencyOptions[0]?.value || DEFAULT_CURRENCY;
    setForm(emptyForm(preferredCurrency));
    setShowModal(true);
  };

  const openEditModal = (budget: BudgetRow) => {
    setEditingBudget(budget);
    setErrors({});
    setForm({
      category: budget.category,
      target: String(budget.target),
      currency: budget.currency,
    });
    setShowModal(true);
  };

  return (
    <section className="p-5 flex justify-center">
      <div className="container-site space-y-3">
        <div className="flex flex-wrap gap-2 justify-between items-end">
          <div>
            <h3 className="heading-2">Budgets</h3>
            <p className="sub-heading-2">
              Assign monthly limits per category and track how you're doing.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Input
              type="text"
              placeholder="Search category or currency..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-56"
            />
            <button
              onClick={openCreateModal}
              className="btn-ghost p-2"
              aria-label="Add budget"
              title="Add Budget"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="border border-dashed border-white/10 bg-white/5 backdrop-blur p-4">
            <div className="text-white/70 text-sm">Total Monthly Budget</div>
            <div className="mt-2 space-y-1 text-xl font-semibold text-white">
              {Object.keys(totalsByCurrency).length === 0 ? (
                <span className="text-sm text-white/50">No budgets yet</span>
              ) : (
                Object.entries(totalsByCurrency).map((currency: any) => {
                  const totals = totalsByCurrency[currency];

                  return (
                    <div key={`budget-${currency}`}>
                      {formatAmount(totals.allocated, currency)}
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-2 text-xs text-white/50">
              Across all categories
            </div>
          </div>
          <div className="border border-dashed border-white/10 bg-white/5 backdrop-blur p-4">
            <div className="text-white/70 text-sm">Spent This Month</div>
            <div className="mt-2 space-y-1 text-xl font-semibold text-white">
              {Object.keys(totalsByCurrency).length === 0 ? (
                <span className="text-sm text-white/50">No data yet</span>
              ) : (
                Object.entries(totalsByCurrency).map((currency: any) => {
                  const totals = totalsByCurrency[currency];

                  return (
                    <div key={`spent-${currency}`}>
                      {formatAmount(totals.spent, currency)}
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-2 text-xs text-white/50">
              Based on recorded expenses
            </div>
          </div>
          <div className="border border-dashed border-white/10 bg-white/5 backdrop-blur p-4">
            <div className="text-white/70 text-sm">Remaining</div>
            <div className="mt-2 space-y-1 text-xl font-semibold text-white">
              {Object.keys(totalsByCurrency).length === 0 ? (
                <span className="text-sm text-white/50">No data yet</span>
              ) : (
                Object.entries(totalsByCurrency).map((currency: any) => {
                  const totals = totalsByCurrency[currency];
                  const remaining = totals.allocated - totals.spent;
                  return (
                    <div
                      key={`remaining-${currency}`}
                      className={remaining < 0 ? "text-red-400" : "text-white"}
                    >
                      {remaining < 0
                        ? `-${formatAmount(Math.abs(remaining), currency)}`
                        : formatAmount(remaining, currency)}
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-2 text-xs text-white/50">
              Overages show in red
            </div>
          </div>
        </div>

        {visibleRows.length === 0 ? (
          <div className="py-10 text-center text-white/50 border border-white/10 bg-white/5">
            <p>No budgets match your search yet.</p>
            <p className="text-sm mt-1">
              Add one with the "New Budget" button above.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleRows.map((row) => (
              <div
                key={row.id}
                className="border border-dashed border-white/10 bg-white/5 backdrop-blur p-4 flex flex-wrap items-center gap-6 justify-between"
              >
                <div className="text-lg font-semibold text-white">
                  {categoryLookup[row.category] || row.category}
                </div>

                <div className="flex-1 min-w-[180px]">
                  <div className="flex flex-col-2 gap-3 text-sm text-white/80">
                    <div>
                      <div className="text-xs uppercase tracking-wide text-white/50">
                        Budget
                      </div>
                      <div className="font-medium text-white">
                        {formatAmount(row.target, row.currency)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-white/50">
                        Spent
                      </div>
                      <div
                        className={
                          row.spent > row.target
                            ? "text-red-400 font-medium"
                            : "text-white font-medium"
                        }
                      >
                        {formatAmount(row.spent, row.currency)}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-white/50">
                        Remaining
                      </div>
                      <div
                        className={
                          row.remaining < 0
                            ? "text-red-400 font-medium"
                            : "text-emerald-400 font-medium"
                        }
                      >
                        {row.remaining >= 0
                          ? formatAmount(row.remaining, row.currency)
                          : `-${formatAmount(
                              Math.abs(row.remaining),
                              row.currency
                            )}`}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wide text-white/50">
                        Progress
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              row.progress >= 100
                                ? "bg-red-400"
                                : "bg-indigo-500"
                            }`}
                            style={{ width: `${row.progress}%` }}
                          />
                        </div>
                        <span className="text-xs text-white/60 w-10 text-right">
                          {Math.round(row.progress)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Edit budget"
                    onClick={() => openEditModal(row)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label="Remove budget"
                    onClick={() => handleRemove(row.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <Modal
          isOpen={showModal}
          onClose={() => closeModal()}
          title={editingBudget ? "Edit Budget" : "New Budget"}
        >
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <Label className="mb-1 block">Category</Label>
              <SimpleSelect
                value={form.category}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, category: value }))
                }
                options={categories.map((category) => ({
                  label: category.name,
                  value: category.id,
                }))}
                placeholder="Select category"
              />
              {errors.category && (
                <p className="mt-1 text-xs text-red-400">{errors.category}</p>
              )}
            </div>

            <div>
              <Label className="mb-1 block" htmlFor="budget-amount">
                Amount
              </Label>
              <Input
                id="budget-amount"
                type="number"
                min="0"
                step="0.01"
                value={form.target}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, target: event.target.value }))
                }
                placeholder="0.00"
              />
              {errors.target && (
                <p className="mt-1 text-xs text-red-400">{errors.target}</p>
              )}
            </div>

            <div>
              <Label className="mb-1 block">Currency</Label>
              <SimpleSelect
                value={form.currency}
                onValueChange={(value) =>
                  setForm((prev) => ({ ...prev, currency: value }))
                }
                options={currencyOptions}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => closeModal()}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingBudget ? "Save changes" : "Create budget"}
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    </section>
  );
}
